import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";

export const compareJobSteps = (js1, js2) => js1.jobid?.toString() === js2.jobid?.toString() && js1.stepid === js2.stepid;

const jobProcedureSlice = createSlice({
    name: 'jobProcedure',
    initialState: {
        procedures: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.forEach(jstep => delete state.procedures[jstep.jobid]);
            action.payload.forEach(jstep => {
                if (!state.procedures[jstep.jobid]) {
                    state.procedures[jstep.jobid] = [];
                }
                const idx = state.procedures[jstep.jobid?.toString()].findIndex(js => compareJobSteps(js, jstep));
                if (idx >= 0) {
                    state.procedures[jstep.jobid].splice(idx, 1, jstep);
                } else {
                    state.procedures[jstep.jobid].push(jstep);
                }
            });
        },
        addOrUpdate: (state, action) => {
            for (const step of ensureArray(action.payload)) {
                if (state.procedures[step.jobid.toString()]) {
                    const idx = state.procedures[step.jobid.toString()].findIndex(js => compareJobSteps(js, step));
                    if (idx >= 0) {
                        state.procedures[step.jobid.toString()].splice(idx, 1, step);
                    } else {
                        state.procedures[step.jobid.toString()].push(step);
                    }
                } else {
                    state.procedures[step.jobid.toString()] = [step];
                }
            }
        },
        remove: (state, action) => {
            for (const step of ensureArray(action.payload)) {
                if (state.procedures[step.jobid?.toString()]) {
                    const idx = state.procedures[step.jobid?.toString()].findIndex(js => compareJobSteps(js, step));
                    if (idx >= 0) {
                        state.procedures[step.jobid?.toString()].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobProcedureSlice.getInitialState());
    }
});

// #region network operations
const fetchJobSteps = jobid => network => network.get('/api/v1/jobProcedureSteps/' + encodeURIComponent(jobid), jobProcedureSlice.actions.set);
export const completeStep = (network, jobid, stepid) => network.post('/api/v1/jobProcedureStep', { jobid, stepid }, jobProcedureSlice.actions.addOrUpdate);
export const uncompleteStep = (network, jobid, stepid) => network.delete(`/api/v1/jobProcedureStep/${encodeURIComponent(jobid)}/${encodeURIComponent(stepid)}`, jobProcedureSlice.actions.remove);
// #endregion

// #region selectors
const selectAllSteps = state => state.jobProcedure.procedures;

const selectStepsByJobid = createSelector([selectAllSteps, (steps, jobid) => jobid], (steps, jobid) => steps[jobid?.toString()] ?? []);
const selectJobSteps = jobid => state => selectStepsByJobid(state, jobid);
// #endregion

// #region listeners
const listen = {
    jobProcedureStepCompleted: (step, { dispatch }) => dispatch(jobProcedureSlice.actions.addOrUpdate(step)),
    jobProcedureStepUncompleted: (step, { dispatch }) => dispatch(jobProcedureSlice.actions.remove(step))
}
// #endregion

export const [useJobSteps] = dataHookFactory(selectJobSteps, initMapFactory(fetchJobSteps, listen));

const reducerDetails = { name: jobProcedureSlice.name, reducer: jobProcedureSlice.reducer };
export default reducerDetails;